import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconPlus, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { usePermissions } from '../auth/usePermissions';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listSuppliers } from '../suppliers/api';
import { listPurchaseOrders, type PurchaseOrderDto, type PurchaseOrderStatus } from './api';
import { NewPurchaseOrderModal } from './NewPurchaseOrderModal';
import { PurchaseOrderStatusBadge } from './PurchaseOrderStatusBadge';

/** Open means "still expecting goods", which is what a buyer is looking at most of the time. */
type Scope = 'open' | 'all';

/** Sentinel for the supplier filter, which is still a filter. */
const ALL = 'all';

/**
 * Purchase orders — the list.
 *
 * Opens on the orders still expecting goods rather than on everything ever raised, because that is
 * the working set: a closed order from March is history, not work.
 */
export function PurchaseOrdersRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false);

  const [scope, setScope] = useState<Scope>('open');
  // The warehouse comes from the header, not from a filter on this screen. There used to be an
  // "All warehouses" option here; it cannot mean that any more — permissions resolve per warehouse,
  // so a list pooled across them would show rows the session has no rights to, and naming a
  // different warehouse than the header earns a 403.
  const warehouseId = useCurrentWarehouseId();
  const [supplierId, setSupplierId] = useState<string>(ALL);
  const [search, setSearch] = useState('');

  const canWrite = permissions.has('po:write');

  const suppliers = useQuery({
    queryKey: ['suppliers', 'all'] as const,
    queryFn: ({ signal }) => listSuppliers({ includeInactive: true }, signal),
  });

  const orders = useQuery({
    queryKey: ['purchase-orders', scope, warehouseId, supplierId] as const,
    queryFn: ({ signal }) =>
      listPurchaseOrders(
        {
          openOnly: scope === 'open',
          warehouseId,
          supplierId: supplierId === ALL ? undefined : supplierId,
        },
        signal,
      ),
  });

  // Client-side, over what the server already narrowed: the buyer usually knows the PO number or
  // the supplier, and a round trip per keystroke to re-ask a question already answered is waste.
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return orders.data ?? [];
    return (orders.data ?? []).filter(
      (o) =>
        o.poNumber.toLowerCase().includes(needle) ||
        o.supplierName.toLowerCase().includes(needle) ||
        o.supplierNumber.toLowerCase().includes(needle) ||
        (o.externalPoNumber ?? '').toLowerCase().includes(needle),
    );
  }, [orders.data, search]);

  function money(value: number, currency: string): string {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  }

  function when(iso: string | null): string {
    return iso ? new Date(iso).toLocaleDateString() : '—';
  }

  /**
   * The PO number stays the way in — an Anchor, not a separate Open button, because opening the
   * order is the only thing anybody does with a row. On a card it is the headline, so the whole
   * headline is the tap target, which is what a thumb wants.
   */
  const columns: DataColumn<PurchaseOrderDto>[] = useMemo(
    () => [
      {
        key: 'poNumber',
        label: t('purchaseOrders.poNumber'),
        card: 'title',
        render: (o) => (
          <>
            <Anchor
              fw={500}
              onClick={() =>
                void navigate({
                  to: '/app/purchase-orders/$id',
                  params: { id: o.purchaseOrderId },
                })
              }
            >
              {o.poNumber}
            </Anchor>
            {o.externalPoNumber && (
              <Text size="xs" c="dimmed">
                {t('purchaseOrders.externalShort', { number: o.externalPoNumber })}
              </Text>
            )}
          </>
        ),
      },
      {
        key: 'supplier',
        label: t('purchaseOrders.supplier'),
        card: 'subtitle',
        render: (o) => (
          <>
            <Text size="sm">{o.supplierName}</Text>
            <Text size="xs" c="dimmed">
              {o.supplierNumber}
            </Text>
          </>
        ),
      },
      {
        key: 'status',
        label: t('purchaseOrders.status'),
        card: 'aside',
        render: (o) => <PurchaseOrderStatusBadge status={o.status as PurchaseOrderStatus} />,
      },
      {
        key: 'warehouse',
        label: t('purchaseOrders.warehouse'),
        render: (o) => o.warehouseCode,
      },
      {
        key: 'orderDate',
        label: t('purchaseOrders.orderDate'),
        render: (o) => when(o.orderDateUtc),
      },
      {
        key: 'expected',
        label: t('purchaseOrders.expected'),
        render: (o) => when(o.expectedReceiptDateUtc),
      },
      {
        key: 'lines',
        label: t('purchaseOrders.lines'),
        align: 'right',
        // An order with no lines orders nothing and cannot be issued, so it is worth calling out
        // rather than showing a quiet 0.
        render: (o) =>
          o.lineCount === 0 ? (
            <Badge color="yellow" variant="light">
              {t('purchaseOrders.noLines')}
            </Badge>
          ) : (
            o.lineCount
          ),
      },
      {
        key: 'total',
        label: t('purchaseOrders.total'),
        align: 'right',
        render: (o) => money(o.totalAmount, o.currencyCode),
      },
    ],
    [t, navigate],
  );

  return (
    <>
      <PageHeader
        title={t('nav.purchaseOrders')}
        actions={
          canWrite && (
            <Button
              color="invetrak"
              leftSection={<IconPlus size={16} />}
              onClick={openCreate}
            >
              {t('purchaseOrders.new')}
            </Button>
          )
        }
      />

      {/* No "you have no warehouses" branch here any more: WarehouseGate does not render a
          screen until the session is in one, so this used to be unreachable code describing a
          state the shell now handles for every screen at once. */}
      <Stack gap="md">
          <Group gap="sm" align="flex-end" wrap="wrap">
            <SegmentedControl
              value={scope}
              onChange={(v) => setScope(v as Scope)}
              data={[
                { value: 'open', label: t('purchaseOrders.scopeOpen') },
                { value: 'all', label: t('purchaseOrders.scopeAll') },
              ]}
            />
            <Select
              label={t('purchaseOrders.filterSupplier')}
              data={[
                { value: ALL, label: t('purchaseOrders.allSuppliers') },
                ...(suppliers.data ?? []).map((s) => ({
                  value: s.supplierId,
                  label: `${s.supplierNumber} — ${s.supplierName}`,
                })),
              ]}
              value={supplierId}
              onChange={(v) => setSupplierId(v ?? ALL)}
              allowDeselect={false}
              searchable
              w={{ base: '100%', sm: 280 }}
            />
            <TextInput
              label={t('purchaseOrders.search')}
              placeholder={t('purchaseOrders.searchPlaceholder')}
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={{ base: '100%', sm: 260 }}
            />
          </Group>

          {orders.isLoading ? (
            <Center py="xl">
              <Loader color="invetrak" />
            </Center>
          ) : orders.isError ? (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('purchaseOrders.errors.listFailed')}
            </Alert>
          ) : visible.length === 0 ? (
            <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
              {scope === 'open' ? t('purchaseOrders.noneOpen') : t('purchaseOrders.none')}
            </Alert>
          ) : (
            <ResponsiveTable
              rows={visible}
              columns={columns}
              rowKey={(o) => o.purchaseOrderId}
              minWidth={900}
              striped
              highlightOnHover
            />
          )}
      </Stack>

      <NewPurchaseOrderModal
        opened={createOpen}
        onClose={closeCreate}
        onCreated={(po) =>
          void navigate({ to: '/app/purchase-orders/$id', params: { id: po.purchaseOrderId } })
        }
      />
    </>
  );
}
